import * as React from "react"
import {
  FaArrowRight,
  FaBars,
  FaCheck,
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaStar,
} from "react-icons/fa"
import TradeIcon from "../TradeIcon"
import QuoteForm from "./QuoteForm"
import { buildView } from "./view-model"
import "../../../styles/templates/base.css"
import "../../../styles/templates/clean.css"
import "../../../styles/templates/clean.mobile.css"
import "../../../styles/templates/port.css"

/**
 * Free Template 3 — the Mockup Maker's `clean` design, ported — MTC-FCS-041.
 * ============================================================================
 * Light editorial: numbered service rows, a marquee strip, generous white
 * space. Same DOM and same stylesheet as
 * `source--mockup-maker/src/templates/clean`, rendered from this builder's
 * draft; clean.css is copied verbatim.
 *
 * ── THE TESTIMONIAL IS NOT PORTED ──────────────────────────────────────────
 * The original carries a written review from "Sarah M." as mockup furniture.
 * A mockup can show a stand-in quote; a contractor's real website cannot —
 * that is a fabricated review on a real business. The block is left out until
 * there is somewhere to type a genuine one.
 */

/**
 * ── LOCAL OR REMOTE — FCS-057(b) ───────────────────────────────────────────
 * DEFAULT_MODE is the switch this design runs in when nobody says otherwise,
 * and it is "local": the content is whatever the builder hands over, which is
 * the draft somebody is editing.
 *
 * In "remote" mode the same component draws a PUBLISHED site, and the JSON
 * fetched from Storage is the only source of anything — text, photographs,
 * contact details, colours. Nothing here reaches for a sample value in that
 * mode, because a placeholder on a real contractor's domain is a sentence
 * they never wrote on a website customers are reading. The refusing is done
 * before this point, in lib/site-content.js: a file that is missing a
 * required field never reaches a template at all.
 *
 * TEMPLATE_VERSION is what a published file is checked against, so a site
 * published against an older shape of this design is refused rather than
 * rendered wrong.
 */
export const DEFAULT_MODE = "local"
export const TEMPLATE_ID = "mm-clean"
export const TEMPLATE_VERSION = "1"

const Photo = ({ src, slot, alt }) =>
  src ? (
    <div className="mk-photo" data-slot={slot}>
      <img src={src} alt={alt || ""} />
    </div>
  ) : (
    <div className="mk-photo" data-slot={slot} aria-hidden="true" />
  )

const TemplateClean = ({ draft, plan, mode = DEFAULT_MODE }) => {
  const v = buildView(draft, { plan, mode })

  const navItems = [
    { href: "#top", label: "Home" },
    { href: "#services", label: "Services" },
    ...(v.hasGallery ? [{ href: "#gallery", label: "Our Work" }] : []),
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ]

  // The marquee is the trade and the work offered — facts, repeated so the
  // strip has something to scroll.
  const marquee = [v.trade, ...v.services.map(s => s.title)].filter(Boolean)

  // Only what the contractor told us. ("One team for the whole job", "Clear, itemised pricing" and "Tidy site, every day" were drawn
  // for everybody: nobody said so. FCS-124)
  const checks = v.serviceArea ? [`Serving ${v.serviceArea}`] : []

  const hasFooterContact = v.hasPhone || v.hasEmail || Boolean(v.serviceArea)

  return (
    <div className="mk mk-clean" style={v.style} data-mode="light" id="top">
      <header className="cl-header">
        <div className="cl-container cl-header-inner">
          <div className="cl-logo">
            <span className="mk-monogram">{v.initials}</span>
            <span className="cl-logo-text">
              <span className="cl-logo-name">
                {v.lead} {v.accent && <em>{v.accent}</em>}
              </span>
              {v.slogan && <span className="cl-logo-tag">{v.slogan}</span>}
            </span>
          </div>

          <nav className="cl-nav">
            {navItems.map((link, i) => (
              <a key={link.label} href={link.href} className={i === 0 ? "is-active" : undefined}>
                {link.label}
              </a>
            ))}
          </nav>

          <div className="cl-header-right">
            {v.hasPhone && (
              <a href={v.phoneHref} className="cl-header-phone">
                <span className="cl-header-phone-icon">
                  <FaPhoneAlt />
                </span>
                <span className="cl-header-phone-text">
                  <small>Call us today</small>
                  <strong>{v.phone}</strong>
                </span>
              </a>
            )}
            <a href="#contact" className="cl-btn cl-btn-primary">
              Get a Quote
            </a>
            <span className="cl-burger">
              <FaBars />
            </span>
          </div>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────── */}
      <section className="cl-hero">
        <div className="cl-container cl-hero-inner">
          <div>
            {v.slogan && <span className="cl-eyebrow">{v.slogan}</span>}

            <h1 className="cl-hero-title">
              {v.trade}
              {v.cityIsReal && (
                <>
                  {" "}
                  <em>in {v.city}</em>
                </>
              )}
            </h1>

            {v.aboutShort && <p className="cl-hero-desc">{v.aboutShort}</p>}

            <div className="cl-hero-actions">
              {v.hasPhone && (
                <a href={v.phoneHref} className="cl-btn cl-btn-primary">
                  <FaPhoneAlt /> {v.phone}
                </a>
              )}
              <a
                href="#services"
                className={v.hasPhone ? "cl-btn cl-btn-ghost" : "cl-btn cl-btn-primary"}
              >
                Our Services <FaArrowRight />
              </a>
            </div>

            {/* "Free estimates" and "Local & independent" were drawn here for every contractor. Nobody said either. (FCS-124) */}
          </div>

          <div className="cl-hero-media">
            <div className="cl-hero-photo">
              <Photo src={v.heroImage} slot="hero" />
            </div>
          </div>
        </div>
      </section>

      <div className="cl-marquee">
        <div className="cl-marquee-track">
          {marquee.map((item, i) => (
            <span className="cl-marquee-item" key={`${item}-${i}`}>
              {item}
              <FaStar />
            </span>
          ))}
        </div>
      </div>

      {/* ── SERVICES ───────────────────────────── */}
      <section className="cl-services" id="services">
        <div className="cl-container">
          <div className="cl-section-head">
            <div>
              <span className="cl-eyebrow">What we do</span>
              <h2 className="cl-h2">What we do</h2>
            </div>
            {v.serviceArea && <p className="cl-lead">Serving {v.serviceArea}.</p>}
          </div>

          <div className="cl-rows">
            {v.services.map((service, i) => (
              <article className="cl-row" key={service.slug}>
                <span className="cl-row-num">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="cl-row-title">{service.title}</h3>
                  <p className="cl-row-desc">{service.description}</p>
                </div>
                <div className="cl-row-media">
                  <Photo src={service.image} slot={`service${i + 1}`} />
                  {!service.image && (
                    <span className="cl-row-glyph">
                      <TradeIcon name={service.icon} />
                    </span>
                  )}
                </div>
                <span className="cl-row-go">
                  <FaArrowRight />
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR WORK ───────────────────────────── */}
      {v.hasGallery && (
        <section className="cl-work" id="gallery">
          <div className="cl-container cl-work-grid">
            <div className="cl-work-intro">
              <span className="cl-eyebrow">Our Work</span>
              <p className="cl-lead">
                Have a look through recent jobs and see the standard of finish for yourself.
              </p>
            </div>
            <div className="cl-work-tiles">
              {v.gallery.slice(0, 6).map((url, i) => (
                <figure className="cl-work-tile mk-tile" key={`${url}-${i}`}>
                  <Photo src={url} slot={`gallery${i + 1}`} />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── ABOUT ──────────────────────────────── */}
      <section className="cl-about" id="about">
        <div className="cl-container cl-about-grid">
          <div className="cl-about-media">
            <div className="cl-about-photo">
              <Photo src={v.heroImage} slot="hero" />
            </div>
          </div>

          <div>
            <span className="cl-eyebrow">About us</span>
            <h2 className="cl-h2">{v.name}</h2>

            {v.aboutText && (
              <p className="cl-lead" style={{ marginTop: 18 }}>
                {v.aboutText}
              </p>
            )}

            {checks.length > 0 && (
              <div className="cl-checks">
                {checks.map((check, i) => (
                  <span className="cl-check" key={`${check}-${i}`}>
                    <FaCheck /> {check}
                  </span>
                ))}
              </div>
            )}

            <div className="cl-about-foot">
              <a href="#contact" className="cl-btn cl-btn-primary">
                Get your free quote <FaArrowRight />
              </a>
              {v.hasPhone && (
                <a href={v.phoneHref} className="cl-about-contact">
                  <span className="mk-monogram">{v.initials}</span>
                  <span className="cl-about-contact-text">
                    <strong>{v.phone}</strong>
                    <span>Call any time for a quote</span>
                  </span>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CLOSING CALL TO ACTION ─────────────── */}
      <section className="cl-cta" id="contact">
        <div className="cl-container cl-cta-inner">
          <div>
            <h2>
              Ready to get started?
              <br />
              <em>Get a free quote today.</em>
            </h2>
            <p>Tell us what you need and we will come back to you.</p>
          </div>
          <div className="cl-cta-actions">
            {v.hasPhone && (
              <a href={v.phoneHref} className="cl-btn cl-btn-primary">
                <FaPhoneAlt /> {v.phone}
              </a>
            )}
            {(v.hasEmail || v.hasPhone) && (
              <a
                href={v.hasEmail ? v.emailHref : v.phoneHref}
                className={v.hasPhone ? "cl-btn cl-btn-ghost" : "cl-btn cl-btn-primary"}
              >
                Request a callback
              </a>
            )}
          </div>
        </div>
      </section>


      {/* ── REQUEST A QUOTE — MTC-FCS-017 ──────── */}
      {v.wantsForm && (
        <section className="mk-quote-section" id="quote" data-premium="leadform">
          <div className="mk-quote-inner">
            <div className="mk-quote-head">
              <h2 className="cl-h2">Request a Quote</h2>
              <p className="mk-quote-lead">
                Tell us what you need and we will come back to you.
              </p>
            </div>
            <QuoteForm view={v} />
          </div>
        </section>
      )}
      {/* ── FOOTER ─────────────────────────────── */}
      <footer className="cl-footer">
        <div className="cl-container">
          <div className="cl-footer-grid" data-cols={hasFooterContact ? 4 : 3}>
            <div>
              <div className="cl-footer-logo">
                <span className="mk-monogram">{v.initials}</span>
                <strong>{v.name}</strong>
              </div>
              {v.aboutShort && <p className="cl-footer-desc">{v.aboutShort}</p>}
              <div className="cl-footer-social">
                <span>
                  <FaFacebookF />
                </span>
                <span>
                  <FaInstagram />
                </span>
                <span>
                  <FaLinkedinIn />
                </span>
              </div>
            </div>

            <div>
              <p className="cl-footer-title">Company</p>
              <div className="cl-footer-links">
                {navItems.map(link => (
                  <a key={link.label} href={link.href}>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <p className="cl-footer-title">Services</p>
              <div className="cl-footer-links">
                {v.services.map(service => (
                  <span key={service.slug}>{service.title}</span>
                ))}
              </div>
            </div>

            {hasFooterContact && (
              <div>
                <p className="cl-footer-title">Get in touch</p>
                <div className="cl-footer-contact">
                  {v.hasPhone && (
                    <span>
                      <FaPhoneAlt /> {v.phone}
                    </span>
                  )}
                  {v.hasEmail && (
                    <span>
                      <FaEnvelope /> {v.email}
                    </span>
                  )}
                  {v.serviceArea && (
                    <span>
                      <FaMapMarkerAlt /> {v.serviceArea}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="cl-footer-bottom">
            <span>
              © {v.year} {v.name}. All rights reserved.
            </span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default TemplateClean
